import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { get } from 'lodash';
import {
  appServiceObjectTypes,
  deleteMapping,
  fetchTestObjectTypesFields,
  saveMappingFopApp,
  testMappingForObjectType,
  updateForObjectType,
} from 'slices/appService.slice';
import { useAppDispatch } from 'slices/store';
import type { TAppName } from 'types/app.type';
import type { TObjectType } from 'types/state.interface';
import CONSTANTS from '@timeedit/types/lib/constants';
import {
  type EApplicationObjectTypeGroup,
  EObjectTypeMappingStatuses,
} from '@timeedit/types/lib/enums';
import { Tag } from '@timeedit/ui-components';
import {
  Button,
  Popconfirm,
  Select,
  Typography,
} from '@timeedit/ui-components/antd';
import './PanelHeader.scss';

// The raw hex colors in `CONSTANTS.ObjectTypeMappingStatusProps` put antd's Tag
// into "custom color" mode (solid fill, white text, no border), bypassing the
// ui-components preset styling. Map each status to the closest design-system
// preset so the status Tag matches every other Tag in the app.
const STATUS_TAG_COLOR: Record<EObjectTypeMappingStatuses, string> = {
  [EObjectTypeMappingStatuses.NOT_TESTED]: 'warning',
  [EObjectTypeMappingStatuses.NOT_MAPPED]: 'warning',
  [EObjectTypeMappingStatuses.MAPPED]: 'cyan',
  [EObjectTypeMappingStatuses.TESTED]: 'success',
  [EObjectTypeMappingStatuses.FAILED]: 'error',
};

type PanelHeaderType = {
  appName: TAppName;
  objectTypeIdx: number;
  objectType: TObjectType;
  objectTypeSaving?: boolean;
};

const PanelHeader = ({
  appName,
  objectTypeIdx,
  objectType,
  objectTypeSaving,
}: PanelHeaderType) => {
  // Memoized variables
  const dispatch = useAppDispatch();
  const dataObjectType = useSelector(appServiceObjectTypes(appName));
  const {
    applicationObjectTypeName,
    applicationObjectTypeLabel,
    applicationObjectTypeGroup,
    status,
  } = useMemo(() => objectType, [objectType]);

  // Callbacks
  const onUpdateLabelCallback = useCallback(
    (applicationObjectTypeLabel: string) =>
      dispatch(
        updateForObjectType({
          appName,
          objectTypeIdx,
          applicationObjectTypeLabel,
        })
      ),
    [appName, objectTypeIdx]
  );

  const onTestCallback = useCallback(
    (e: any) => {
      e.stopPropagation();
      dispatch(
        testMappingForObjectType({
          appName,
          objectTypeIdx,
          objectType,
        })
      );
    },
    [objectType, appName, objectTypeIdx]
  );

  const onUpdateGroupCallback = useCallback(
    (applicationObjectTypeGroup: EApplicationObjectTypeGroup) =>
      dispatch(
        updateForObjectType({
          appName,
          objectTypeIdx,
          applicationObjectTypeGroup,
        })
      ),
    [appName, objectTypeIdx]
  );

  const onSaveCallback = (e: any) => {
    e.stopPropagation();
    dispatch(saveMappingFopApp({ appName }));
    if (objectType.objectTypeExtId) {
      dispatch(
        fetchTestObjectTypesFields({
          appName,
          type: objectType.applicationObjectTypeName,
          objectTypeExtId: objectType.objectTypeExtId,
        })
      );
    }
  };

  const onDeleteCallback = useCallback(
    (e: any) => {
      e.stopPropagation();
      dispatch(
        deleteMapping({
          appName,
          objectTypes: dataObjectType,
          objectTypeIdx: objectTypeIdx,
        })
      );
    },
    [objectType]
  );
  return (
    <div className="panel-header--wrapper">
      <div className="panel-header--label">
        <Typography.Paragraph
          editable={{
            onChange: onUpdateLabelCallback,
          }}
        >
          {applicationObjectTypeLabel || applicationObjectTypeName}
        </Typography.Paragraph>
      </div>
      {applicationObjectTypeName && (
        <div className="panel-header--application-name">
          <span className="value">{applicationObjectTypeName}</span>
        </div>
      )}
      <div className="panel-header--group">
        <Select
          options={[...Object.keys(CONSTANTS.ObjectTypeGroupProps), null].map(
            (key) => ({
              value: key,
              label: key
                ? get(CONSTANTS, `ObjectTypeGroupProps.${key}.label`)
                : '-',
            })
          )}
          popupMatchSelectWidth
          optionFilterProp="label"
          showSearch
          onChange={onUpdateGroupCallback}
          onClick={(e) => e.stopPropagation()}
          value={applicationObjectTypeGroup || null}
        ></Select>
      </div>
      <div className="panel-header--status">
        <Tag color={STATUS_TAG_COLOR[status as EObjectTypeMappingStatuses]}>
          {get(CONSTANTS, `ObjectTypeMappingStatusProps.${status}.label`)}
        </Tag>
      </div>
      <div className="panel-header--actions">
        {appName === 'tePreferences' ? null : (
          <Popconfirm
            title="It needs to be saved before the test!"
            onConfirm={onTestCallback}
            okText="Save and test"
            cancelText="Cancel"
          >
            <Button onClick={(e) => e.stopPropagation()} size="small">
              Test
            </Button>
          </Popconfirm>
        )}
        <Button
          size="small"
          type="primary"
          onClick={onSaveCallback}
          loading={objectType.hasChanges && objectTypeSaving}
          disabled={!objectType.hasChanges || objectTypeSaving}
        >
          Save
        </Button>
        <Popconfirm
          title="Confirm delete?"
          onConfirm={onDeleteCallback}
          okText="Yes"
          cancelText="No"
        >
          {appName === 'teExam' ? null : (
            <Button size="small" type="default" disabled={objectTypeSaving}>
              Delete
            </Button>
          )}
        </Popconfirm>
      </div>
    </div>
  );
};

export default PanelHeader;
